import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { FieldValue } from '../Forms/FormsSharedFields';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-nextjs';
import { getCookie } from 'hooks/useCookie';
import { useForm } from 'hooks/useForm';
import { Column, FormFields } from 'model/Form';
import { useI18n } from 'next-localization';
import { useRouter } from 'next/router';
import { Rows } from './Rows';
import { HoneypotField } from './HoneypotField';
import useHelpers from 'hooks/helpers';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { pushDataLayerEvent } from 'hooks/dataLayerPush';
import { EventData } from 'model/DataLayer';

type FormEntry = { fieldName: string; value: string };
type EloquaFormProps = {
  fields: FormFields;
  rendering?: ComponentRendering;
  background?: string;
  icon?: boolean;
  className?: string;
  onSubmitSuccess?: () => void;
};

const EloquaForm = ({
  fields,
  rendering,
  background = 'background-mist',
  icon = false,
  className = '',
  onSubmitSuccess,
}: EloquaFormProps) => {
  const { sitecoreContext: context } = useSitecoreContext();
  const config = context['articleWildcardConfiguration'];
  const formDataValue = fields?.formJson?.value || config?.bottomContainerFormDataSource?.value;

  const {
    formField,
    honeypotField,
    setHoneypotFieldValue,
    honeypotFieldValue,
    submitRow,
    getColClass,
    layout,
    getButtonText,
  } = useForm(formDataValue);
  const { t } = useI18n(); // For translation support
  const { asPath } = useRouter();
  const { anchorId } = useHelpers(rendering);
  const [formData, setFormData] = useState(new FormData());
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  const formStartEventExistInDataLayer = useRef(false);

  const includeHiddenFields = useCallback(
    (data: FormData) => {
      formField?.forEach((field) => {
        field.cols.forEach((col) => {
          if (!!col.cells[0]) {
            if (col.cells[0]?.element?.displayType === 'hidden') {
              switch (col.cells[0]?.element?.htmlName) {
                case 'Date_submitted':
                  data.set(col.cells[0]?.element?.htmlName, new Date(Date.now()).toDateString());
                  break;
                case 'Module_name':
                  data.set(col.cells[0]?.element?.htmlName, asPath);
                  break;
                default:
                  data.set(col.cells[0]?.element?.htmlName, col.cells[0]?.element?.defaultValue);
              }
            }
          }
        });
      });
    },
    [asPath, formField]
  );
  const setupFormData = useCallback(() => {
    const newFormData = new FormData();
    const { htmlName, siteId } = formDataValue;

    if (htmlName) newFormData.append('elqFormName', htmlName);
    if (siteId) newFormData.append('elqSiteId', siteId);
    newFormData.append('referralUrl', asPath);
    includeHiddenFields(newFormData);
    setFormData(newFormData);
  }, [formDataValue, includeHiddenFields]);

  const showForm = useMemo(() => {
    return formDataValue?.showForm ?? true;
  }, [formDataValue?.showForm]);

  const formName = useMemo(() => {
    return formDataValue?.htmlName;
  }, [formDataValue?.htmlName]);

  useEffect(() => {
    if (!showForm) {
      console.log('Missing data');
    } else {
      setupFormData();
    }
  }, [setupFormData, showForm]);

  const setValue = ({ fieldName: htmlName, value }: FormEntry) => {
    formData.set(htmlName, value);
  };

  const onChange = (value: FieldValue) => {
    formData.delete(value.name);
    if (Array.isArray(value.value)) {
      value.value.forEach((v) => {
        formData.append(value.name, v);
      });
    } else {
      formData.set(value.name, String(value.value));
    }
  };

  const executeFormStartEvent = (payload: EventData) => {
    if (!formStartEventExistInDataLayer.current) {
      formStartEventExistInDataLayer.current = true;
      pushDataLayerEvent(payload); // Data Layer Logic
    }
  };

  const postFormData = (event: FormEvent) => {
    if (!formDataValue.postUrl) return;

    event.preventDefault();
    const eloquaGuid = getCookie('ELOQUA')?.split('=')[1]?.split('&')[0] || ''; // Replace with actual cookie logic
    formData.append('elqCustomerGUID', eloquaGuid);
    formData.append('elqCookieWrite', '0');
    if (honeypotField?.honeypotEnabled) {
      setValue({ fieldName: honeypotField.honeypotFieldName, value: honeypotFieldValue });
    }

    axios
      .post(formDataValue.postUrl, formData)
      .then((res) => {
        if (res.status === 200) {
          onSubmitSuccess?.();
          setSubscriptionSuccess(true);
          setupFormData();
          pushDataLayerEvent({
            event: 'form_submit',
            formName: formName,
            form_field: 'n/a',
          });
        }
      })
      .catch((e) => console.error(e));
  };

  const renderSubmitRow = () => {
    return submitRow?.map((col: Column, i) => (
      <div
        key={`A${i}`}
        className={`${getColClass(col.width)} ${subscriptionSuccess ? 'hidden' : ''}`}
      >
        <AbrdnButton id={col.cells[0].element.id} buttonType="primary">
          <span>{getButtonText(col)}</span>
        </AbrdnButton>
      </div>
    ));
  };

  return (
    <section
      id={anchorId}
      data-e2e-component-uid={rendering?.uid}
      className={`container-fluid default-section-padding ${background} ${className}`}
    >
      {showForm && (
        <form
          className="container relative background-to-change"
          name={formName}
          onSubmit={postFormData}
        >
          <div className="row">
            <Rows
              layout={layout}
              formName={formName}
              icon={icon}
              onChange={onChange}
              onUpdateDataLayer={pushDataLayerEvent}
              onExecuteFormStartEvent={executeFormStartEvent}
            />
          </div>

          {/* Spam protection field */}

          <HoneypotField
            honeypotField={honeypotField}
            value={honeypotFieldValue}
            onChange={setHoneypotFieldValue}
          />

          <div className="row">{renderSubmitRow()}</div>

          {subscriptionSuccess && (
            <div
              className={`container message-content d-flex flex-column justify-content-center align-items-start ${background}`}
            >
              <div className="d-flex">
                <em className="bi bi-check-lg h3 me-2" aria-hidden="true" />
                <p className="h3">{t('formSuccessTitle')}</p>
              </div>

              <span className="body-large success-description mt-6">
                {t('formSuccessDescription')}
              </span>
            </div>
          )}
        </form>
      )}
    </section>
  );
};

export default EloquaForm;
